import React, { useState, useEffect } from 'react';
import './Contact.css';
import { contactService } from '../../api';
import Header from '../Header/Header';
import Footer from '../footer/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    subject: false,
    message: false
  });

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = 'Le nom est requis';
        } else if (value.trim().length < 2) {
          error = 'Le nom doit contenir au moins 2 caractères';
        }
        break;
      case 'email':
        if (!value) {
          error = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Veuillez entrer un email valide';
        }
        break;
      case 'phone':
        if (value && !/^[0-9]{10}$/.test(value)) {
          error = 'Le téléphone doit contenir 10 chiffres';
        }
        break;
      case 'subject':
        if (!value) {
          error = 'Veuillez sélectionner un sujet';
        }
        break;
      case 'message':
        if (!value.trim()) {
          error = 'Le message est requis';
        } else if (value.trim().length < 10) {
          error = 'Le message doit contenir au moins 10 caractères';
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  useEffect(() => {
    const newErrors = {};
    Object.keys(touched).forEach(field => {
      if (touched[field]) {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
        }
      }
    });
    setErrors(newErrors);
  }, [formData, touched]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    const error = validateField(name, formData[name]);
    setErrors(prev => ({
      ...prev,
      [name]: error || undefined
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    setTouched({
      name: true,
      email: true,
      phone: true,
      subject: true,
      message: true
    });
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null, // Envoyer null si vide
        subject: formData.subject,
        message: formData.message,
        // Données pour l'email
        to: 'alphandiaye383@gmail.com',
        emailSubject: `Nouveau contact: ${formData.subject}`,
        emailContent: `
          <h2>Nouveau message de contact</h2>
          <p><strong>Nom:</strong> ${formData.name}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Téléphone:</strong> ${formData.phone || 'Non renseigné'}</p>
          <p><strong>Sujet:</strong> ${formData.subject}</p>
          <p><strong>Message:</strong></p>
          <p>${formData.message}</p>
        `
      };

      await contactService.create(payload);
      
      setSubmitMessage('Message envoyé avec succès! Nous vous répondrons bientôt.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setErrors({});
      setTouched({
        name: false,
        email: false,
        phone: false,
        subject: false,
        message: false
      });
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.status === 422) {
        // Erreurs de validation du serveur
        const serverErrors = error.response.data.errors || {};
        setErrors(serverErrors);
        setSubmitMessage('Veuillez corriger les erreurs dans le formulaire.');
      } else {
        setSubmitMessage(error.response?.data?.message || 'Erreur lors de l\'envoi du message. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <div className="contact-page">
      <Header />
      
      <main className="contact-main-content">
        <div className="contact-container">
          <h1>Contactez-nous</h1>
          {submitMessage && (
            <div className={`alert ${submitMessage.includes('succès') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit} noValidate>
            <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
              <label htmlFor="name">Nom complet *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.name ? 'error-input' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
            
            <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.email ? 'error-input' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            
            <div className={`form-group ${errors.phone ? 'has-error' : ''}`}>
              <label htmlFor="phone">Téléphone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.phone ? 'error-input' : ''}
                placeholder="Ex: 0612345678"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
            
            <div className={`form-group ${errors.subject ? 'has-error' : ''}`}>
              <label htmlFor="subject">Sujet *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.subject ? 'error-input' : ''}
              >
                <option value="">Sélectionnez un sujet</option>
                <option value="Demande d'information">Demande d'information</option>
                <option value="Support technique">Support technique</option>
                <option value="Réclamation">Réclamation</option>
                <option value="Autre">Autre</option>
              </select>
              {errors.subject && <span className="error-message">{errors.subject}</span>}
            </div>
            
            <div className={`form-group ${errors.message ? 'has-error' : ''}`}>
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.message ? 'error-input' : ''}
                rows="5"
              />
              {errors.message && <span className="error-message">{errors.message}</span>}
            </div>
            
            <button 
              type="submit" 
              disabled={isSubmitting}
              className={isSubmitting ? 'submitting' : ''}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Envoi en cours...
                </>
              ) : 'Envoyer le message'}
            </button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;